import moment from "moment";
import { Badge, BadgeSize, BadgeVariant } from "shared/components";
import { capitalize } from "shared/utils";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { Row } from "views/applications-id-report/components/Row";
import { DynamicKeyValue } from "views/applications-id-report/components/SmartRender/DynamicKeyValue";
import {
  OurPetPolicyAnimal,
  OurPetPolicyAnimalType,
  OurPetPolicyLandlordStatus
} from "../../types/our-pet-policy";
import { PetVerificationStatusMap } from "../../constants";
import { useCallback } from "react";
import { statusBadgeVariant } from "../../utils";

interface OurPetPolicyReportFieldsProps {
  animals: OurPetPolicyAnimal[];
}

export const OurPetPolicyReportFields: React.FC<
  OurPetPolicyReportFieldsProps
> = ({ animals }: OurPetPolicyReportFieldsProps) => {
  // opp verification status
  const verificationBadgeVariant = useCallback(
    (status: OurPetPolicyLandlordStatus): BadgeVariant => {
      if (status === OurPetPolicyLandlordStatus.VERIFIED)
        return BadgeVariant.SUCCESS;
      return BadgeVariant.BASE;
    },
    []
  );

  return (
    <div>
      {animals.map((animal, index) => (
        <ContentTitle
          title={`Pet / Assistance animal ${index + 1}`}
          key={`content-${animal.name}-${index}`}
          contentClassName="space-y-3"
        >
          <DynamicKeyValue
            detailsKey="Submitted date"
            value={
              animal.submitted_date
                ? moment(Number(animal.submitted_date) * 1000).format(
                    "MMMM DD, YYYY [at] h:mm A"
                  )
                : undefined
            }
          />
          <DynamicKeyValue detailsKey="name" value={animal.name} />
          <DynamicKeyValue
            detailsKey="Birth date"
            value={
              animal.birth_date
                ? moment(animal.birth_date).format("MMMM DD, YYYY")
                : undefined
            }
            fullDate
          />
          <DynamicKeyValue
            detailsKey="Weight"
            value={
              animal.weight?.value
                ? `${animal.weight.value}${animal.weight.unit?.toLowerCase()}`
                : undefined
            }
          />
          <DynamicKeyValue detailsKey="Breed" value={animal.breed} />
          <DynamicKeyValue detailsKey="Species" value={animal.species} />
          <DynamicKeyValue
            detailsKey="Primary color"
            value={animal.primary_color}
          />
          <DynamicKeyValue
            detailsKey="Gender"
            value={
              animal.gender ? capitalize(animal.gender.toLowerCase()) : "--"
            }
          />
          <Row
            title="OPP verification status"
            value={
              <Badge
                className="shrink-0 items-center"
                size={BadgeSize.SM}
                variant={verificationBadgeVariant(animal.verification_status)}
              >
                {animal.verification_status
                  ? PetVerificationStatusMap[animal.verification_status]
                  : "--"}
              </Badge>
            }
          />
          <DynamicKeyValue
            detailsKey="Converted to pet"
            value={animal.converted_to_pet}
          />
          <DynamicKeyValue
            detailsKey="Type"
            value={animal.type ? OurPetPolicyAnimalType[animal.type] : "--"}
          />
          <Row
            title="Landlord approval"
            value={
              <Badge
                className="shrink-0 items-center"
                size={BadgeSize.SM}
                variant={statusBadgeVariant(animal.status)}
              >
                {animal.status
                  ? capitalize(animal.status.toLowerCase().replace("_", " "))
                  : "--"}
              </Badge>
            }
          />
        </ContentTitle>
      ))}
    </div>
  );
};
